/**
 * Deploy-version awareness.
 *
 * The app is stamped with the build id it was compiled from (`BUILD_ID`). The
 * server serves the current build's id at `/version.json`. Comparing the two
 * tells us, reliably, whether a newer version has shipped since this tab
 * loaded — which is the difference between "reload, it'll fix it" and "the
 * network dropped a file, reloading won't help". The old code reloaded on a
 * blind timer and dead-ended when it guessed wrong.
 */

export const BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? "dev";

let cachedServerVersion: string | null = null;

/**
 * The build id the server is currently serving, or null if it can't be read
 * (offline, blocked, or running from the dev server where the file is absent).
 * Always fetched fresh — the file is served no-store and we bust any
 * intermediary with a query param.
 */
export async function fetchServerVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store",
      signal,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown };
    cachedServerVersion = typeof body.version === "string" ? body.version : null;
    return cachedServerVersion;
  } catch {
    return null;
  }
}

/**
 * True when the server is serving a build different from this one. Unknown
 * (null) is treated as "not newer" — we never act on a failed check.
 */
export async function isNewerVersionDeployed(signal?: AbortSignal): Promise<boolean> {
  const server = await fetchServerVersion(signal);
  if (!server || BUILD_ID === "dev") return false;
  return server !== BUILD_ID;
}

/** The last successfully-read server version, for display without a refetch. */
export function lastKnownServerVersion(): string | null {
  return cachedServerVersion;
}

/**
 * A reload that cannot be answered from a stale cache. The HTML is already
 * no-store, but bumping a query param defeats bfcache and any intermediary, and
 * a fresh HTML document carries the new hashed asset URLs.
 */
export function reloadForNewVersion(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * The `_v` cache-buster is only there to force the navigation; strip it from the
 * address bar once we've booted so it doesn't linger or get bookmarked.
 */
export function stripReloadParam(): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has("_v")) {
      url.searchParams.delete("_v");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    /* ignore */
  }
}
